import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Copy, ArrowRight, CheckCircle2, AlertCircle, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { api, apiErrorMessage } from "@/lib/api";

/**
 * Modal de revisión y fusión de proveedores duplicados.
 *
 * Backend:
 *  - GET /api/providers/duplicates → devuelve grupos (exact + fuzzy) donde
 *    coincide `nombre_comercial` normalizado + (mismo CIF o CIF vacío).
 *  - POST /api/providers/merge {canonical_id, duplicate_ids} → fusiona campos
 *    (unión: rellena huecos del canónico con datos de los duplicados) y
 *    actualiza referencias en `reservas`, `products` y `cotizaciones`.
 *
 * UX:
 *  - Cada grupo muestra las fichas comparadas lado-a-lado.
 *  - Por defecto se marca la ficha más "completa" como canónica; el usuario
 *    puede cambiarla con radio buttons.
 *  - Botón "Fusionar" hace la operación y refresca la lista sin cerrar el modal
 *    (para procesar varios grupos seguidos).
 */

const FIELD_DISPLAY = [
  { key: "nombre_comercial", label: "Comercial" },
  { key: "nombre_sociedad", label: "Sociedad" },
  { key: "cif", label: "CIF" },
  { key: "cuenta_contable", label: "Cta contable" },
  { key: "sistema_reserva", label: "Sistema" },
  { key: "contact_email", label: "Email" },
  { key: "telefono", label: "Teléfono" },
  { key: "country", label: "País" },
  { key: "ciudad", label: "Ciudad" },
];

export function DuplicateProvidersDialog({ open, onOpenChange, onMerged }) {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["providers-duplicates"],
    queryFn: async () => (await api.get("/providers/duplicates")).data,
    enabled: open,
    staleTime: 30_000,
  });
  const groups = data?.groups || [];
  // Cache local de la selección por grupo: {norm_key: canonical_provider_id}
  const [selection, setSelection] = useState({});

  const canonicalOf = (g) => selection[g.norm_key] || g.suggested_canonical_id;

  const mergeMut = useMutation({
    mutationFn: async ({ canonical_id, duplicate_ids }) =>
      (await api.post("/providers/merge", { canonical_id, duplicate_ids })).data,
    onSuccess: (res, vars) => {
      toast.success(
        `Fusionado. ${res.refs_updated?.reservas || 0} reservas, ${res.refs_updated?.products || 0} productos y ${res.refs_updated?.cotizaciones || 0} cotizaciones actualizados.`,
      );
      qc.invalidateQueries({ queryKey: ["providers-duplicates"] });
      onMerged?.();
      refetch();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const handleMerge = (g) => {
    const canonical_id = canonicalOf(g);
    const duplicate_ids = g.providers.map((p) => p.id).filter((id) => id !== canonical_id);
    if (!duplicate_ids.length) return;
    mergeMut.mutate({ canonical_id, duplicate_ids });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-hidden p-0" data-testid="duplicates-dialog">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5 text-primary" />
            Proveedores duplicados
            {data && (
              <Badge variant="secondary" className="ml-2">
                {data.total_groups} {data.total_groups === 1 ? "grupo" : "grupos"} · {data.total_extras} fichas a fusionar
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Se agrupan fichas con el mismo <strong>nombre comercial</strong> normalizado. Se subdivide por CIF: solo son duplicados si comparten CIF o ambos carecen de él. Fusionar rellena los huecos del canónico con datos de los otros y actualiza reservas, productos y cotizaciones.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] px-6 py-4">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32 w-full" />)}
            </div>
          ) : groups.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-center" data-testid="duplicates-empty">
              <CheckCircle2 className="h-12 w-12 text-emerald-500" />
              <p className="text-lg font-semibold">Sin duplicados</p>
              <p className="text-sm text-muted-foreground">Todos los proveedores tienen nombre comercial único o CIF distinto.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {groups.map((g) => {
                const canonicalId = canonicalOf(g);
                return (
                  <div key={g.norm_key + (g.cif_shared || "no")} className="rounded-lg border" data-testid={`dup-group-${g.norm_key}`}>
                    <div className="flex items-center justify-between border-b bg-muted/40 px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{g.display}</p>
                        <Badge variant="secondary" className="text-[10px]">×{g.count}</Badge>
                        {g.fuzzy ? (
                          <Badge className="border-amber-300 bg-amber-100 text-amber-900 text-[10px] hover:bg-amber-100">
                            <AlertCircle className="mr-1 h-3 w-3" />fuzzy
                          </Badge>
                        ) : null}
                        {g.cif_shared ? (
                          <Badge variant="outline" className="text-[10px]">CIF {g.cif_shared}</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">sin CIF</Badge>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleMerge(g)}
                        disabled={mergeMut.isPending}
                        data-testid={`dup-merge-${g.norm_key}`}
                      >
                        {mergeMut.isPending && mergeMut.variables?.canonical_id === canonicalId ? (
                          <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Fusionando…</>
                        ) : (
                          <>Fusionar en canónico <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></>
                        )}
                      </Button>
                    </div>
                    <RadioGroup
                      value={canonicalId}
                      onValueChange={(v) => setSelection((s) => ({ ...s, [g.norm_key]: v }))}
                      className="grid gap-3 p-4 md:grid-cols-2"
                    >
                      {g.providers.map((p) => {
                        const isCanonical = p.id === canonicalId;
                        return (
                          <label
                            key={p.id}
                            htmlFor={`radio-${p.id}`}
                            className={`flex cursor-pointer flex-col gap-1 rounded-md border p-3 transition-colors ${
                              isCanonical
                                ? "border-primary bg-primary/5"
                                : "border-input hover:bg-muted/40"
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              <RadioGroupItem value={p.id} id={`radio-${p.id}`} className="mt-1" />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <p className="truncate font-medium">{p.name || p.raw_name}</p>
                                  {isCanonical && (
                                    <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />
                                  )}
                                </div>
                                <p className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                                  Completitud: {p.completeness} campos · id {p.id.slice(0, 8)}
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-x-3 gap-y-0.5 pl-6 text-xs">
                              {FIELD_DISPLAY.map(({ key, label }) => {
                                const v = p[key];
                                return (
                                  <div key={key} className="truncate">
                                    <span className="text-muted-foreground">{label}: </span>
                                    {v ? (
                                      <span className="font-medium" title={String(v)}>{String(v)}</span>
                                    ) : (
                                      <span className="text-muted-foreground/60">—</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </label>
                        );
                      })}
                    </RadioGroup>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
